"use client";

import { useMemo, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  DataTable,
  Panel,
  TextAreaField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, WIDGET_H2 } from "@/components/tool-kit/styles";
import { CheckList } from "@/components/tools/research-writing/check-list";
import { LengthMeter } from "@/components/tools/research-writing/length-meter";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  buildOpEdDocument,
  emptyPoints,
  measureOpEd,
  OP_ED_PARTS,
  OP_ED_RANGE,
  type OpEdInput,
  type OpEdPoint,
  opEdChecks,
} from "@/lib/tool-kit/research-writing/op-ed-outline";

/** English strings. A translated content file overrides these through `ui`. */
export const OP_ED_OUTLINE_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Clear the form",
  pieceHeading: "Your piece",
  headlineLabel: "Working headline",
  headlinePlaceholder: "e.g. Put Shade Where People Wait for the Bus",
  wordsLabel: "Target length (words)",
  wordsPlaceholder: "750",
  wordsHint:
    "This tool plans for 650–800 words. Outlets set their own limits, so check the one you are pitching.",
  hookLabel: "News hook — why now (a note to yourself)",
  hookPlaceholder: "The event, report or anniversary that makes this timely",
  ledeLabel: "Lede — the opening image or fact",
  ledePlaceholder: "A scene, a number or a moment that pulls the reader in",
  argumentLabel: "Your argument, in one sentence",
  argumentPlaceholder: "Cities should … before they …",
  pointsHeading: "Three points",
  pointsNote:
    "Each point makes a claim, backs it with evidence (one piece per line), and says why it matters. The third can widen the frame instead.",
  pointLabel: "Point {n}",
  claimLabel: "The claim",
  claimPlaceholder: "A sentence that moves the argument forward",
  evidenceLabel: "Evidence — one piece per line",
  evidencePlaceholder:
    "A statistic, a study, an expert, or first-hand experience",
  soWhatLabel: "So what? (optional)",
  soWhatPlaceholder: "Why the reader should care",
  endHeading: "The objection and the ending",
  objectionLabel: "The strongest objection (“to be sure”)",
  objectionPlaceholder: "Critics will say … and they have a point.",
  responseLabel: "Your answer to it",
  responsePlaceholder: "But …",
  actionLabel: "Conclusion and call to action",
  actionPlaceholder:
    "What the reader or decision-maker should do, ending on a line that echoes the opening",
  resultsHeading: "Length and checks",
  meterLabel: "Words against your target",
  meterText: "one: {words} of {target} word|other: {words} of {target} words",
  partsHead: { part: "Part", target: "Target words", yours: "Your words" },
  checksLabel: "Checklist",
  checkStates: { done: "Done", todo: "To do", note: "Check" },
  checkText: {
    hook_done: "There is a news hook.",
    hook_note:
      "No news hook yet. Editors want a reason this piece runs now: an event, a report, a date.",
    lede_done: "The lede is written.",
    lede_todo: "Write the lede: the image or fact that opens the piece.",
    argument_done: "The argument is stated.",
    argument_todo: "State your argument.",
    oneSentence_done: "The argument is one sentence.",
    oneSentence_note:
      "The argument runs to more than one sentence. Readers should be able to repeat it.",
    points_done: "All three points have a claim.",
    points_todo: "Point {items} has no claim yet.",
    evidence_done: "Every claim has evidence.",
    evidence_note: "Point {items} makes a claim without evidence.",
    objection_done: "The objection and your answer are both in.",
    objection_todo: "Add the strongest objection and your answer to it.",
    action_done: "There is a call to action.",
    action_todo: "End on what the reader should do.",
    range_done: "Within 650–800 words.",
    range_todo: "Write something to measure the length.",
    range_note:
      "Outside 650–800 words. Check the outlet’s limit and trim or fill.",
  },
  fileName: "op-ed-outline",
  document: {
    heading: "Your op-ed outline",
    prompt: "To write",
    guidance: "Tip",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Every part has your content.",
  },
  defaultTitle: "Op-Ed Outline",
  subtitle: "Op-ed outline · {words} words",
  headings: {
    budget: "Word Budget",
    lede: "Lede",
    argument: "Argument",
    point1: "Point 1",
    point2: "Point 2",
    point3: "Point 3",
    objection: "To be sure",
    conclusion: "Conclusion and call to action",
  },
  target: "Target: about {words} words.",
  tips: {
    lede: "Open with a scene, a fact or a moment, and tie it to the news that makes the piece timely.",
    argument: "One sentence the reader can repeat to someone else.",
    point1:
      "Claim, evidence, and what it means. Lead with your strongest point.",
    point2:
      "A different kind of evidence from the first: not a second helping of the same.",
    point3:
      "Another claim, or widen the frame with history or the global picture.",
    objection:
      "Meet the strongest objection fairly, then say why your argument still stands.",
    conclusion: "Say what should happen next, and close by echoing the lede.",
  },
  prompts: {
    hook: "Name the news hook: the event, report, anniversary or trend that makes this piece timely.",
    lede: "Open with a scene, a number or a moment that makes the reader care.",
    argument:
      "State your argument in one sentence: “[Who] should [do what] because [why].”",
    claim: "Make the claim: a sentence that moves the argument forward.",
    evidence:
      "Give the evidence: a statistic, a study, an expert, first-hand experience.",
    soWhat: "Say why the reader should care.",
    objection:
      "State the strongest objection: “Critics will say … and they have a point.”",
    response: "Answer it: “But …”",
    action:
      "Say what the reader or decision-maker should do, and echo your opening.",
  },
  hookLabelDoc: "News hook",
  claimLead: "Claim:",
  soWhatLead: "So what:",
  objectionLead: "Objection:",
  responseLead: "Answer:",
  budgetHead: { part: "Part", target: "Target words", yours: "Your words" },
  budgetTotal: "Total",
  example: {
    headline: "Put Shade Where People Wait for the Bus",
    words: "750",
    newsHook: "[A recent heat wave or local heat-safety report, with its date]",
    lede: "At three o’clock on a July afternoon, the bench at [a real bus stop] is too hot to sit on. The shelter has no shade, and the bus is late.",
    argument:
      "Cities should put shade where people wait for buses before they plant it anywhere else.",
    point1Claim: "Heat is a transit problem, not only a park problem.",
    point1Evidence:
      "[Temperatures measured at stops and at nearby spots, with the source]\n[Share of riders without another way to travel, with the source]",
    point1SoWhat: "People who rely on the bus cannot choose where to stand.",
    point2Claim: "A citywide average hides the stops.",
    point2Evidence:
      "[The city’s average tree canopy, with the source]\n[Canopy cover at stops, with the source]",
    point2SoWhat:
      "A city can meet its canopy target and still leave its stops bare.",
    point3Claim: "A shelter can be built faster than a tree can grow.",
    point3Evidence:
      "[Cost and time to install a shelter]\n[Years for a young tree to shade a bench]",
    point3SoWhat: "Shade can start this summer.",
    objection:
      "Trees do more than shade a bench: they cool whole neighborhoods and clean the air.",
    response:
      "That is true, and the planting should go on. But the people at the stops are exposed this summer, and a shelter can be built before the next heat wave.",
    action:
      "Ask your council to audit shade at every stop and to fund shelters where there is none. The next time you wait for a bus, look up.",
  },
} as const;

const EMPTY: OpEdInput = {
  headline: "",
  words: "",
  newsHook: "",
  lede: "",
  argument: "",
  points: emptyPoints(),
  objection: "",
  response: "",
  action: "",
};

const POINT_KEYS = ["first", "second", "third"] as const;

type TextKey = Exclude<keyof OpEdInput, "points">;
type PointKey = keyof OpEdPoint;

export default function OpEdOutlineGeneratorWidget() {
  const strings = useToolUi(OP_ED_OUTLINE_GENERATOR_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [input, setInput] = useState<OpEdInput>(EMPTY);
  const labels = useMemo(
    () => ({ ...strings, hookLabel: strings.hookLabelDoc }),
    [strings]
  );
  const doc = useMemo(
    () => buildOpEdDocument(input, labels, fmt),
    [input, labels, fmt]
  );
  const documentLabels = useMemo(() => strings.document, [strings]);
  const meter = measureOpEd(input);
  const checkText = strings.checkText as Record<string, string>;
  const checks = opEdChecks(input).map((check) => ({
    key: check.id,
    state: check.state,
    text: t(checkText[`${check.id}_${check.state}`] ?? "", {
      items: (check.items ?? []).join(", "),
    }),
  }));
  const rows = [
    ...OP_ED_PARTS.map((part) => [
      strings.headings[part],
      fmt(meter.targets[part]),
      fmt(meter.parts[part]),
    ]),
    [strings.budgetTotal, fmt(meter.target), fmt(meter.total)],
  ];
  const update = (key: TextKey) => (value: string) =>
    setInput((previous) => ({ ...previous, [key]: value }));
  const updatePoint = (index: number, key: PointKey, value: string) =>
    setInput((previous) => ({
      ...previous,
      points: previous.points.map((point, i) =>
        i === index ? { ...point, [key]: value } : point
      ),
    }));
  const loadExample = () => {
    const e = strings.example;
    setInput({
      ...EMPTY,
      headline: e.headline,
      words: e.words,
      newsHook: e.newsHook,
      lede: e.lede,
      argument: e.argument,
      points: [
        {
          claim: e.point1Claim,
          evidence: e.point1Evidence,
          soWhat: e.point1SoWhat,
        },
        {
          claim: e.point2Claim,
          evidence: e.point2Evidence,
          soWhat: e.point2SoWhat,
        },
        {
          claim: e.point3Claim,
          evidence: e.point3Evidence,
          soWhat: e.point3SoWhat,
        },
      ],
      objection: e.objection,
      response: e.response,
      action: e.action,
    });
  };
  const area = (
    key: TextKey,
    id: string,
    label: string,
    placeholder: string,
    rows = 2
  ) => (
    <TextAreaField
      id={`oe-${id}`}
      label={label}
      onChange={update(key)}
      placeholder={placeholder}
      prose
      rows={rows}
      value={input[key]}
    />
  );

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={loadExample}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setInput(EMPTY)}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      <Panel title={strings.pieceHeading}>
        <div className="grid gap-4 sm:grid-cols-[1fr_12rem]">
          <TextField
            id="oe-headline"
            label={strings.headlineLabel}
            onChange={update("headline")}
            placeholder={strings.headlinePlaceholder}
            value={input.headline}
          />
          <TextField
            hint={strings.wordsHint}
            id="oe-words"
            label={strings.wordsLabel}
            numeric
            onChange={update("words")}
            placeholder={strings.wordsPlaceholder}
            value={input.words}
          />
        </div>
        {area("newsHook", "hook", strings.hookLabel, strings.hookPlaceholder)}
        {area("lede", "lede", strings.ledeLabel, strings.ledePlaceholder)}
        {area(
          "argument",
          "argument",
          strings.argumentLabel,
          strings.argumentPlaceholder
        )}
      </Panel>

      <Panel title={strings.pointsHeading}>
        <p className="text-muted-foreground text-xs">{strings.pointsNote}</p>
        {input.points.map((point, index) => (
          <fieldset
            className="space-y-3 rounded-lg border border-border p-4"
            key={POINT_KEYS[index]}
          >
            <legend className="px-1 font-medium text-foreground text-sm">
              {t(strings.pointLabel, { n: index + 1 })}
            </legend>
            <TextAreaField
              id={`oe-p${index}-claim`}
              label={strings.claimLabel}
              onChange={(value) => updatePoint(index, "claim", value)}
              placeholder={strings.claimPlaceholder}
              prose
              rows={2}
              value={point.claim}
            />
            <TextAreaField
              id={`oe-p${index}-evidence`}
              label={strings.evidenceLabel}
              onChange={(value) => updatePoint(index, "evidence", value)}
              placeholder={strings.evidencePlaceholder}
              prose
              rows={3}
              value={point.evidence}
            />
            <TextAreaField
              id={`oe-p${index}-sowhat`}
              label={strings.soWhatLabel}
              onChange={(value) => updatePoint(index, "soWhat", value)}
              placeholder={strings.soWhatPlaceholder}
              prose
              rows={2}
              value={point.soWhat}
            />
          </fieldset>
        ))}
      </Panel>

      <Panel title={strings.endHeading}>
        {area(
          "objection",
          "objection",
          strings.objectionLabel,
          strings.objectionPlaceholder
        )}
        {area(
          "response",
          "response",
          strings.responseLabel,
          strings.responsePlaceholder
        )}
        {area(
          "action",
          "action",
          strings.actionLabel,
          strings.actionPlaceholder,
          3
        )}
      </Panel>

      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.resultsHeading}</h2>
        <LengthMeter
          label={strings.meterLabel}
          max={meter.target}
          over={meter.total > OP_ED_RANGE.high || meter.total > meter.target}
          value={meter.total}
          valueText={tPlural(strings.meterText, locale, meter.target, {
            words: fmt(meter.total),
            target: fmt(meter.target),
          })}
        />
        <DataTable
          head={[
            strings.partsHead.part,
            strings.partsHead.target,
            strings.partsHead.yours,
          ]}
          rows={rows}
        />
        <CheckList
          items={checks}
          label={strings.checksLabel}
          stateLabels={strings.checkStates}
        />
      </section>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="oe"
        labels={documentLabels}
      />
    </div>
  );
}
